// Drawola Studio — "Wireframe" built-in template. Fork-only.
//
// A low-fidelity app/web screen skeleton: top bar, left nav, a row of content
// cards and a primary action. Greys come from upstream's element palette so
// the whole thing inverts cleanly in dark mode.

import { ROUNDNESS } from "@excalidraw/common";
import { convertToExcalidrawElements } from "@excalidraw/excalidraw";

import type { ExcalidrawElementSkeleton } from "@excalidraw/element/transform";

import { FILL, STROKE, templateAppState } from "./shared";

import type { TemplateScene } from "./types";

const ROUNDED = { type: ROUNDNESS.ADAPTIVE_RADIUS } as const;

// screen box
const X = 120;
const Y = 120;
const W = 760;
const H = 520;

const HEADER_H = 64;
const SIDEBAR_W = 200;

/** A flat placeholder block — the wireframe's "lorem ipsum". */
const bar = (
  x: number,
  y: number,
  width: number,
  height: number,
  backgroundColor: string = FILL.GRAY,
): ExcalidrawElementSkeleton => ({
  type: "rectangle",
  x,
  y,
  width,
  height,
  backgroundColor,
  strokeColor: STROKE.MUTED,
  strokeWidth: 1,
});

const CARD_W = 160;
const CARD_GAP = 16;
const CARD_X = X + SIDEBAR_W + 24;
const CARD_Y = Y + HEADER_H + 32;
const CARD_TITLES = ["Card one", "Card two", "Card three"];

export const buildWireframeTemplate = (): TemplateScene => {
  const cards = CARD_TITLES.flatMap((title, i): ExcalidrawElementSkeleton[] => {
    const cardX = CARD_X + i * (CARD_W + CARD_GAP);
    return [
      {
        type: "rectangle",
        x: cardX,
        y: CARD_Y,
        width: CARD_W,
        height: 150,
        backgroundColor: FILL.TRANSPARENT,
        strokeColor: STROKE.MUTED,
        roundness: ROUNDED,
      },
      bar(cardX + 16, CARD_Y + 16, CARD_W - 32, 72),
      {
        type: "text",
        x: cardX + 16,
        y: CARD_Y + 102,
        text: title,
        fontSize: 16,
      },
    ];
  });

  const skeleton: ExcalidrawElementSkeleton[] = [
    // screen frame
    {
      type: "rectangle",
      x: X,
      y: Y,
      width: W,
      height: H,
      backgroundColor: FILL.TRANSPARENT,
      roundness: ROUNDED,
    },

    // top bar
    bar(X, Y, W, HEADER_H, FILL.GRAY),
    {
      type: "ellipse",
      x: X + 24,
      y: Y + 16,
      width: 32,
      height: 32,
      backgroundColor: FILL.BLUE,
      strokeColor: STROKE.MUTED,
    },
    {
      type: "text",
      x: X + 68,
      y: Y + 22,
      text: "Product",
      fontSize: 20,
    },
    bar(X + 516, Y + 22, 64, 20, FILL.GRAY_FAINT),
    bar(X + 584, Y + 22, 64, 20, FILL.GRAY_FAINT),
    bar(X + 652, Y + 22, 64, 20, FILL.GRAY_FAINT),

    // left nav
    bar(X, Y + HEADER_H, SIDEBAR_W, H - HEADER_H, FILL.GRAY_FAINT),
    bar(X + 24, Y + HEADER_H + 32, SIDEBAR_W - 48, 28),
    bar(X + 24, Y + HEADER_H + 76, SIDEBAR_W - 48, 28),
    bar(X + 24, Y + HEADER_H + 120, SIDEBAR_W - 48, 28),
    bar(X + 24, Y + HEADER_H + 164, SIDEBAR_W - 48, 28),

    // content
    ...cards,

    // primary action
    {
      type: "rectangle",
      x: CARD_X,
      y: CARD_Y + 190,
      width: 176,
      height: 44,
      backgroundColor: FILL.BLUE,
      roundness: ROUNDED,
      label: { text: "Get started", fontSize: 16 },
    },
  ];

  return {
    elements: convertToExcalidrawElements(skeleton),
    appState: templateAppState(),
  };
};
